// frontend/src/components/ExportModal.jsx
import React, { useState } from 'react';
import {
    XIcon,
    DownloadIcon,
    CheckIcon,
    DocumentDownloadIcon,
    TableIcon
} from '@heroicons/react/outline';

const ExportModal = ({ isOpen, onClose, onExport, exporting, assessments = [] }) => {
    // ═══════════════════════════════════════════════════════════
    // FACTOR DEFINITIONS
    // These are the 7 EBCSS factors. When exporting, the backend
    // will produce one column per factor (name + raw score) for
    // each candidate.
    // ═══════════════════════════════════════════════════════════
    const factorFields = [
        { key: 'factor_ESST_1', factorId: 'ESST-1', label: 'Emotional Stability & Stress Tolerance' },
        { key: 'factor_IEB_2', factorId: 'IEB-2', label: 'Integrity & Ethical Behaviour' },
        { key: 'factor_RCD_3', factorId: 'RCD-3', label: 'Rule Compliance & Work Discipline' },
        { key: 'factor_WCR_4', factorId: 'WCR-4', label: 'Work Commitment & Reliability' },
        { key: 'factor_SBAC_5', factorId: 'SBAC-5', label: 'Social Behaviour & Aggression Control' },
        { key: 'factor_AMLC_6', factorId: 'AMLC-6', label: 'Adaptability to Migrant Living Conditions' },
        { key: 'factor_IFTLA_7', factorId: 'IFTLA-7', label: 'Instruction Following & Task Learning Ability' },
    ];

    // ═══════════════════════════════════════════════════════════
    // AVAILABLE FIELDS
    // ═══════════════════════════════════════════════════════════
    const availableFields = [
        // Personal Information
        { key: 'full_name', label: 'Full Name', defaultChecked: true, category: 'Personal' },
        { key: 'email', label: 'Email', defaultChecked: true, category: 'Personal' },
        { key: 'phone', label: 'Phone Number', defaultChecked: true, category: 'Personal' },
        { key: 'date_of_birth', label: 'Date of Birth', defaultChecked: false, category: 'Personal' },
        { key: 'age', label: 'Age', defaultChecked: false, category: 'Personal' },
        { key: 'sex', label: 'Sex', defaultChecked: false, category: 'Personal' },
        { key: 'address', label: 'Address', defaultChecked: false, category: 'Personal' },

        // Assessment Information
        { key: 'status', label: 'Status', defaultChecked: true, category: 'Assessment' },
        { key: 'assessment_name', label: 'Assessment', defaultChecked: true, category: 'Assessment' },
        { key: 'total_score', label: 'Total Score', defaultChecked: true, category: 'Assessment' },
        { key: 'grade', label: 'Grade', defaultChecked: true, category: 'Assessment' },
        { key: 'recommendation', label: 'Recommendation', defaultChecked: true, category: 'Assessment' },
        { key: 'meaning', label: 'Meaning', defaultChecked: false, category: 'Assessment' },

        // ⭐ NEW: Factor Scores (all selected by default)
        ...factorFields.map(f => ({
            key: f.key,
            label: f.label,
            defaultChecked: true,
            category: 'Factors',
            isFactor: true,
            factorId: f.factorId,
        })),

        // Work Information
        { key: 'department', label: 'Department', defaultChecked: true, category: 'Work' },
        { key: 'job_position', label: 'Job Position', defaultChecked: false, category: 'Work' },
        { key: 'education', label: 'Education', defaultChecked: false, category: 'Work' },

        // Timestamps
        { key: 'started_at', label: 'Started At', defaultChecked: false, category: 'Timestamps' },
        { key: 'completed_at', label: 'Completed At', defaultChecked: true, category: 'Timestamps' }
    ];

    // ═══════════════════════════════════════════════════════════
    // CATEGORY METADATA (order matters for display)
    // ═══════════════════════════════════════════════════════════
    const categories = {
        'Personal': { icon: '👤', label: 'Personal Information' },
        'Assessment': { icon: '📊', label: 'Assessment Results' },
        'Factors': { icon: '🧩', label: 'Factor Scores' },        // ⭐ NEW
        'Work': { icon: '💼', label: 'Work Information' },
        'Timestamps': { icon: '🕐', label: 'Timestamps' },
    };

    const [selectedFields, setSelectedFields] = useState(
        availableFields.filter(f => f.defaultChecked).map(f => f.key)
    );

    const [selectAll, setSelectAll] = useState(false);

    const [expandedCategories, setExpandedCategories] = useState({
        'Personal': true,
        'Assessment': true,
        'Factors': true,          // ⭐ expanded by default
        'Work': true,
        'Timestamps': true
    });

    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handleToggleField = (fieldKey) => {
        setSelectedFields(prev => {
            const newSelection = prev.includes(fieldKey)
                ? prev.filter(f => f !== fieldKey)
                : [...prev, fieldKey];

            setSelectAll(newSelection.length === availableFields.length);
            return newSelection;
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedFields([]);
            setSelectAll(false);
        } else {
            setSelectedFields(availableFields.map(f => f.key));
            setSelectAll(true);
        }
    };

    const handleCategorySelectAll = (category) => {
        const categoryFields = availableFields.filter(f => f.category === category);
        const categoryKeys = categoryFields.map(f => f.key);
        const allSelected = categoryKeys.every(key => selectedFields.includes(key));

        if (allSelected) {
            setSelectedFields(prev => prev.filter(key => !categoryKeys.includes(key)));
        } else {
            const newFields = [...selectedFields];
            categoryKeys.forEach(key => {
                if (!newFields.includes(key)) {
                    newFields.push(key);
                }
            });
            setSelectedFields(newFields);
        }
    };

    const isCategoryAllSelected = (category) => {
        const categoryFields = availableFields.filter(f => f.category === category);
        const categoryKeys = categoryFields.map(f => f.key);
        return categoryKeys.every(key => selectedFields.includes(key));
    };

    const handleExport = () => {
        if (selectedFields.length === 0) {
            alert('Please select at least one field to export');
            return;
        }

        // ═══════════════════════════════════════════════════════
        // Split the selection into "regular fields" and "factors"
        // so the backend can distinguish them.
        // ═══════════════════════════════════════════════════════
        const factorSelections = factorFields
            .filter(f => selectedFields.includes(f.key))
            .map(f => ({ factorId: f.factorId, label: f.label, key: f.key }));

        const regularFields = selectedFields.filter(k => !k.startsWith('factor_'));

        onExport({
            fields: regularFields,
            factors: factorSelections,
            // Also pass the raw list for backwards compatibility
            selectedFields,
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center">
                        <DocumentDownloadIcon className="h-6 w-6 text-[#063b56] mr-2" />
                        <h3 className="text-lg font-bold text-[#063b56]">Export Candidates</h3>
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Excel Format
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <XIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Field Selection */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="mb-4 flex flex-wrap justify-between items-center gap-2">
                        <p className="text-sm text-gray-600">
                            Select the fields you want to include in the Excel export
                        </p>
                        <button
                            onClick={handleSelectAll}
                            className="text-sm text-[#ff9f2f] hover:text-[#e68a1a] font-medium"
                        >
                            {selectAll ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>

                    <div className="space-y-4">
                        {Object.entries(categories).map(([category, info]) => {
                            const categoryFields = availableFields.filter(f => f.category === category);
                            const allSelected = isCategoryAllSelected(category);

                            return (
                                <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => toggleCategory(category)}
                                        className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span className="text-lg">{info.icon}</span>
                                            <span className="font-medium text-gray-700">{info.label}</span>
                                            <span className="text-xs text-gray-400">
                                                ({categoryFields.filter(f => selectedFields.includes(f.key)).length}/{categoryFields.length})
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCategorySelectAll(category);
                                                }}
                                                className="text-xs text-[#ff9f2f] hover:text-[#e68a1a] font-medium"
                                            >
                                                {allSelected ? 'Deselect All' : 'Select All'}
                                            </button>
                                            <span className="text-gray-400">
                                                {expandedCategories[category] ? '▼' : '▶'}
                                            </span>
                                        </div>
                                    </button>

                                    {expandedCategories[category] && (
                                        <div className={`p-3 grid ${category === 'Factors' ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3'} gap-2`}>
                                            {categoryFields.map((field) => (
                                                <label
                                                    key={field.key}
                                                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedFields.includes(field.key)}
                                                        onChange={() => handleToggleField(field.key)}
                                                        className="h-4 w-4 text-[#ff9f2f] focus:ring-[#ff9f2f] border-gray-300 rounded flex-shrink-0"
                                                    />
                                                    <div className="min-w-0">
                                                        <span className="text-sm text-gray-700 block truncate">
                                                            {field.label}
                                                        </span>
                                                        {field.isFactor && (
                                                            <span className="text-[10px] text-gray-400 font-mono">
                                                                {field.factorId}
                                                            </span>
                                                        )}
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {selectedFields.length === 0 && (
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg text-sm">
                            ⚠️ Please select at least one field to export
                        </div>
                    )}

                    <div className="mt-4 p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                        <p className="text-xs text-gray-500">
                            <strong>Selected:</strong> {selectedFields.length} of {availableFields.length} fields
                        </p>
                        <div className="flex items-center space-x-2 text-xs text-gray-400">
                            <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                <CheckIcon className="h-3 w-3 mr-0.5" />
                                Ready to export
                            </span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={exporting || selectedFields.length === 0}
                        className="px-6 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] disabled:opacity-50 flex items-center shadow-sm transition-colors"
                    >
                        {exporting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Exporting...
                            </>
                        ) : (
                            <>
                                <DownloadIcon className="h-5 w-5 mr-2" />
                                Export Excel
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportModal;